import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  TitleContainer,
  Title,
  Wrapper,
  RatesContainer,
  RateRow,
  CurrencySelectWrapper,
  RateInputWrapper,
  ArrowWrapper,
  DeleteButtonWrapper,
  AddButtonWrapper,
  ApplyWrapper,
} from './FixedExchangeRatesModal.styled';
import Button from 'components/Button/Button';
import { Modal } from 'components/Modals/Modal/Modal';
import { useGetCurrencyOptions } from '../helpers';
import { Select } from 'components/Select/Select';
import { Trash, Plus, ArrowRight } from '@phosphor-icons/react';
import Icon from 'components/Icon/Icon';
import Input from 'components/Input/Input';
import { COLORS } from 'assets/styled';
import {
  IExchangeRateRow,
  buildFixedExchangeRates,
  isFormValid,
  parseExistingRates,
  createEmptyRow,
} from './helpers';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { IFixedExchangeRates } from 'types/Company.types';

interface IFixedExchangeRatesModalProps {
  onCancel: () => void;
  onSave: (rates: IFixedExchangeRates) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  fixedExchangeRates?: IFixedExchangeRates;
  companyCurrencyCode?: string;
}

export const FixedExchangeRatesModal = ({
  isOpen,
  setIsOpen,
  onCancel,
  onSave,
  fixedExchangeRates,
  companyCurrencyCode,
}: IFixedExchangeRatesModalProps) => {
  const { t } = useTranslation();
  const currencyOptions = useGetCurrencyOptions();
  const { isSmallTablet } = useBreakpointFlag();
  const [rateRows, setRateRows] = useState<IExchangeRateRow[]>([]);

  useEffect(() => {
    const rows = parseExistingRates(fixedExchangeRates);
    setRateRows(rows.length > 0 ? rows : [createEmptyRow(companyCurrencyCode)]);
  }, [fixedExchangeRates, companyCurrencyCode]);

  const handleAddRow = () => {
    setRateRows([...rateRows, createEmptyRow(companyCurrencyCode)]);
  };

  const handleRemoveRow = (id: string) => {
    setRateRows(rateRows.filter((row) => row.id !== id));
  };

  const handleUpdateRow = (
    id: string,
    field: keyof IExchangeRateRow,
    value: string
  ) => {
    setRateRows(
      rateRows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleSave = () => {
    const rates = buildFixedExchangeRates(rateRows);
    onSave(rates);
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      modalStyle={{
        position: 'fixed',
        width: isSmallTablet ? '90%' : '800rem',
        minWidth: isSmallTablet ? 'auto' : 'auto',
        margin: 'auto',
      }}
      level="SECOND"
    >
      <Container>
        <TitleContainer>
          <Title>{t('Fixed Exchange Rates')}</Title>
        </TitleContainer>
        <Wrapper>
          <RatesContainer>
            {rateRows.map((row) => (
              <RateRow key={row.id}>
                <CurrencySelectWrapper>
                  <Select
                    name={`from-currency-${row.id}`}
                    options={currencyOptions.filter(
                      (opt) => opt.value !== companyCurrencyCode
                    )}
                    controlledValue={
                      currencyOptions.find(
                        (opt) => opt.value === row.fromCurrency
                      ) || null
                    }
                    onChange={(option: any) =>
                      handleUpdateRow(
                        row.id,
                        'fromCurrency',
                        option?.value || ''
                      )
                    }
                    placeholder={t('From currency')}
                    width="100%"
                    translate={false}
                  />
                </CurrencySelectWrapper>
                <ArrowWrapper>
                  <ArrowRight size={20} />
                </ArrowWrapper>
                <CurrencySelectWrapper>
                  <Select
                    name={`to-currency-${row.id}`}
                    options={currencyOptions}
                    controlledValue={
                      currencyOptions.find(
                        (opt) => opt.value === row.toCurrency
                      ) || null
                    }
                    onChange={(option: any) =>
                      handleUpdateRow(row.id, 'toCurrency', option?.value || '')
                    }
                    placeholder={t('To currency')}
                    width="100%"
                    translate={false}
                    isDisabled={true}
                  />
                </CurrencySelectWrapper>
                <RateInputWrapper>
                  <Input
                    type="number"
                    value={row.rate}
                    onChange={(e) =>
                      handleUpdateRow(row.id, 'rate', e.target.value)
                    }
                    placeholder={t('Rate')}
                    wrapperStyles={{ width: '100%' }}
                    hideNumberArrows
                  />
                </RateInputWrapper>
                <DeleteButtonWrapper>
                  <Icon
                    svg={Trash}
                    color={COLORS.RED}
                    size={22}
                    onClick={() => handleRemoveRow(row.id)}
                  />
                </DeleteButtonWrapper>
              </RateRow>
            ))}
          </RatesContainer>
          <AddButtonWrapper>
            <Button
              onClick={handleAddRow}
              secondary
              width="auto"
              label={t('Add exchange rate')}
              icon={<Plus size={18} />}
            />
          </AddButtonWrapper>
        </Wrapper>
        <ApplyWrapper>
          <Button
            onClick={onCancel}
            secondary
            width="200rem"
            label={t('Cancel')}
          />
          <Button
            disabled={!isFormValid(rateRows)}
            onClick={handleSave}
            primary
            width="200rem"
            label={t('Save')}
          />
        </ApplyWrapper>
      </Container>
    </Modal>
  );
};
